/**
 * The Pick'em New contest dialog's rules (ISSUE-1429): the starting draft,
 * what is still missing, the request body and where each refusal goes.
 * Pure: no React, no fetch.
 *
 * Owner pick 2026-11-01: the next Pick'em contest is a new contest in the same
 * season, started from New contest exactly like Survivor. The draft copies the
 * last contest's settings and leaves the dates for the server: a blank start
 * date starts the day after the last contest ended, or today if that is later.
 */
import type { usePickemContestCreate } from "@sportsmet/contest-sdk/private";

import { NEW_CONTEST_NAME_REFUSED_NOTE } from "../newContest/newContestErrors";
import {
  NEW_CONTEST_NAME_MISSING_ITEM,
  validateContestName,
} from "../newContest/newContestModel";
import {
  buildPickemSetupPayload,
  pickemDraftFromSetup,
  pickemSetupMissingItems,
  type typePickemSetupDraft,
  type typePickemSetupPayload,
} from "./pickemCreateSetupDraft";
import { pickemFieldErrorMessage } from "./PickemSettingsFields";

export type typePickemContestCreateBody = Parameters<
  ReturnType<typeof usePickemContestCreate>["mutateAsync"]
>[0];

export type typePickemNewContestDraft = {
  name: string;
  setup: typePickemSetupDraft;
};

export const PICKEM_NEW_CONTEST_START_HINT =
  "Leave the start date blank to start the day after your last contest ended.";
export const PICKEM_NEXT_CONTEST_LINE =
  "This contest is over. Start the next one whenever you're ready.";
export const PICKEM_NEW_CONTEST_UNSETTLED_NOTE =
  "Your current contest is still going. Once it ends and you confirm the winner, you can start the next one.";
export const PICKEM_NEW_CONTEST_NOT_READY_NOTE =
  "This league hasn't posted its season schedule yet. Try again once the games are out.";
export const PICKEM_NEW_CONTEST_OVERLAP_NOTE =
  "These dates overlap another contest in this pool. Pick a later start date.";
export const PICKEM_NEW_CONTEST_NO_SEASON_NOTE =
  "This pool has no season to add a contest to.";
export const PICKEM_NEW_CONTEST_FORBIDDEN_NOTE =
  "Only the Commissioner and Admins can start a contest.";

/**
 * The starting draft: the last contest's settings with the dates cleared, or
 * the league's starting values when the pool never had a contest. null while
 * neither has loaded.
 */
export function buildPickemNewContestDraft(input: {
  lastSettings: typePickemSetupPayload | null;
  leagueDefaults: typePickemSetupPayload | null;
  leagueKey: string;
}): typePickemNewContestDraft | null {
  const source = input.lastSettings ?? input.leagueDefaults;
  if (source === null) return null;
  const setup = pickemDraftFromSetup({
    setup: source,
    leagueKey: input.leagueKey,
  });
  return {
    name: "",
    setup: {
      ...setup,
      startDate: null,
      endDate: null,
      endDateMode: "tbd",
    },
  };
}

/** Plain-English items the footer lists while Create is disabled. */
export function pickemNewContestMissingItems(
  draft: typePickemNewContestDraft,
): readonly string[] {
  const missing: string[] = [];
  if (!validateContestName(draft.name).isValid) {
    missing.push(NEW_CONTEST_NAME_MISSING_ITEM);
  }
  return [...missing, ...pickemSetupMissingItems(draft.setup)];
}

export function isPickemNewContestDirty(
  draft: typePickemNewContestDraft,
  baseline: typePickemNewContestDraft,
): boolean {
  return JSON.stringify(draft) !== JSON.stringify(baseline);
}

/** The request body, or null while the name or a setting is missing. */
export function buildPickemContestCreateBody(input: {
  poolId: string;
  draft: typePickemNewContestDraft;
}): typePickemContestCreateBody | null {
  const name = validateContestName(input.draft.name);
  const setup = buildPickemSetupPayload(input.draft.setup);
  if (!name.isValid || setup === null) return null;
  return { poolId: input.poolId, name: name.trimmed, setup };
}

export type typePickemNewContestFailure =
  | { kind: "name"; message: string }
  | { kind: "inline"; note: string }
  | { kind: "modal" };

/** Where a refusal from /pickem/contest-create shows. */
export function pickemNewContestFailure(input: {
  code: string | null;
  reason: string | null;
}): typePickemNewContestFailure {
  const { code, reason } = input;
  if (code === "VALIDATION_ERROR" && reason === "name") {
    return { kind: "name", message: NEW_CONTEST_NAME_REFUSED_NOTE };
  }
  if (code === "VALIDATION_ERROR" && reason !== null && reason !== "") {
    if (reason === "contestType") return { kind: "modal" };
    return { kind: "inline", note: pickemFieldErrorMessage(reason) };
  }
  if (code === "UNSETTLED_CONTEST_EXISTS") {
    return { kind: "inline", note: PICKEM_NEW_CONTEST_UNSETTLED_NOTE };
  }
  if (code === "CONTEST_TYPE_NOT_READY") {
    return { kind: "inline", note: PICKEM_NEW_CONTEST_NOT_READY_NOTE };
  }
  if (code === "CONTEST_DATES_OVERLAP") {
    return { kind: "inline", note: PICKEM_NEW_CONTEST_OVERLAP_NOTE };
  }
  if (code === "NOT_FOUND" && reason === "season") {
    return { kind: "inline", note: PICKEM_NEW_CONTEST_NO_SEASON_NOTE };
  }
  if (code === "FORBIDDEN_ROLE") {
    return { kind: "inline", note: PICKEM_NEW_CONTEST_FORBIDDEN_NOTE };
  }
  return { kind: "modal" };
}

/** Whether the Game tab's empty state offers New contest to this viewer. */
export function canOfferPickemNewContest(input: {
  emptyStateReason: "noContest" | "noRoundsYet" | "contestEnded";
  isCommissioner: boolean;
}): boolean {
  return (
    input.isCommissioner &&
    (input.emptyStateReason === "noContest" ||
      input.emptyStateReason === "contestEnded")
  );
}
